import { Component, OnInit, Input, EventEmitter, Output, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { FederalTax } from "src/app/models/taxes";
import { MaskService } from "src/app/services/mask.service";
import { MaskType } from "src/app/constants/masks";
import { ChangeRequestFederalTaxModel } from "src/app/models/change-requests";
import { ListItem } from "src/app/models/listItem";
import { HelperService } from "src/app/services/helper.service";
import { PayrollService } from "src/app/services/payroll.service";
import { ChangeRequestStatus } from "src/app/constants/change-request";
import { LookupService } from "src/app/services/lookup.service";

@Component({
    selector: 'mwp-federal-tax-form',
    templateUrl: './federal-tax-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollFederalTaxFormComponent implements OnInit {
    @Input() title: String;
    @Input() isEditing: boolean;
    @Input() federalTax: FederalTax;
    @Input() ChangeRequestFederalTaxModel: ChangeRequestFederalTaxModel;
    @Output() public cancelEmit: EventEmitter<ChangeRequestFederalTaxModel> = new EventEmitter<ChangeRequestFederalTaxModel>();

    public federalTaxesForm: FormGroup;
    public maskType = MaskType;
    public federalFilingStatus: ListItem[];
    public isDenied: boolean;
    public deniedReason: string;
    public lastUpdatedOn: string;
    public effectiveMinDate: Date;

    constructor(
        private maskService: MaskService,
        private formBuilder: FormBuilder,
        private helperService: HelperService,
        private payrollService: PayrollService,
        private lookupService: LookupService
    ) {
        this.effectiveMinDate = new Date();
        this.lookupService.getFederalFilingStatuses().subscribe((response : ListItem[]) => {
            this.federalFilingStatus = response;
        });
    }

    ngOnInit(): void {
        if (this.ChangeRequestFederalTaxModel) {
            this.federalTaxesForm = this.formBuilder.group({
                effectiveOn: [{ value: this.ChangeRequestFederalTaxModel.effectiveDate, disabled: !this.isEditing }, Validators.required],
                filingStatus: [{ value: this.ChangeRequestFederalTaxModel.filingStatusID.toString(), disabled: !this.isEditing }, Validators.required],
                onlyTwoJobs: [{ disabled: !this.isEditing },],
                claimTaxExempt: [{ disabled: !this.isEditing },],
                claimDependents: [{ value: this.ChangeRequestFederalTaxModel.claimDependents, disabled: !this.isEditing },],
                otherIncome: [{ value: this.ChangeRequestFederalTaxModel.otherIncome, disabled: !this.isEditing },],
                deductions: [{ value: this.ChangeRequestFederalTaxModel.deductions, disabled: !this.isEditing },],
                extraWithholding: [{ value: this.ChangeRequestFederalTaxModel.extraWithholding, disabled: !this.isEditing },],
                isFedTaxExempt: [{ value: this.ChangeRequestFederalTaxModel.isFedTaxExempt, disabled: !this.isEditing },],
                isTwoJobs: [{ value: this.ChangeRequestFederalTaxModel.twoJobIndicator, disabled: !this.isEditing },],
            });

            if(this.ChangeRequestFederalTaxModel.changeRequestStatusID == ChangeRequestStatus.denied) {
                this.isDenied = true;
                this.deniedReason = this.ChangeRequestFederalTaxModel.changeRequestDeniedReason;
            }
        } else {
            this.federalTaxesForm = this.formBuilder.group({
                effectiveOn: [{ value: this.federalTax.effectiveDate, disabled: !this.isEditing }, Validators.required],
                filingStatus: [{ value: this.federalTax.filingStatusID.toString(), disabled: !this.isEditing }, Validators.required],
                onlyTwoJobs: [{ disabled: !this.isEditing },],
                claimTaxExempt: [{ disabled: !this.isEditing },],
                claimDependents: [{ value: this.federalTax.claimDependents, disabled: !this.isEditing },],
                otherIncome: [{ value: this.federalTax.otherIncome, disabled: !this.isEditing },],
                deductions: [{ value: this.federalTax.deductions, disabled: !this.isEditing },],
                extraWithholding: [{ value: this.federalTax.extraWithholding, disabled: !this.isEditing },],
                isFedTaxExempt: [{ value: this.federalTax.isFedTaxExempt, disabled: !this.isEditing },],
                isTwoJobs: [{ value: this.federalTax.twoJobIndicator, disabled: !this.isEditing },],
            });

            if (this.federalTax.lastUpdated)
                this.lastUpdatedOn = this.federalTax.lastUpdated;
        }
    }

    removeCurrencyAndConvertToNumber(value: string) {
        const formattedValue = this.maskService.removeCurrencyMask(value);
        return Number(formattedValue);
    }

    public save(): void {
        let l_payload: ChangeRequestFederalTaxModel;
        
        if (this.ChangeRequestFederalTaxModel)
            l_payload = this.ChangeRequestFederalTaxModel;
        else
            l_payload = new ChangeRequestFederalTaxModel();

        l_payload.effectiveDate = this.helperService.convertDateToPayloadString(this.federalTaxesForm.get("effectiveOn").value); // converts the value from datepicker to same format as received from API
        l_payload.filingStatusID = this.federalTaxesForm.get('filingStatus').value;
        l_payload.twoJobIndicator = this.federalTaxesForm.get('isTwoJobs').value;
        l_payload.isFedTaxExempt = this.federalTaxesForm.get('isFedTaxExempt').value;
        l_payload.claimDependents =  this.maskService.removeCurrencyMask(this.federalTaxesForm.get('claimDependents').value);
        l_payload.otherIncome = Number(this.maskService.removeCurrencyMask(this.federalTaxesForm.get('otherIncome').value));
        l_payload.deductions = this.maskService.removeCurrencyMask(this.federalTaxesForm.get('deductions').value);
        l_payload.extraWithholding = Number(this.maskService.removeCurrencyMask(this.federalTaxesForm.get('extraWithholding').value));

        this.payrollService.saveFederalTaxChange(l_payload).subscribe((response: ChangeRequestFederalTaxModel) => {
            this.cancelEmit.emit(response);
        });
    }

    public cancel() {
        this.cancelEmit.emit();
    }

}


